import { FC, useMemo } from "react";
import { Box, Flex, Table, Text, Button, Badge } from "@radix-ui/themes";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";

import { atlasRoutes } from "@/app/router/routes";
import { SmartEngineRunSummary, useSmartEngineRuns } from "./use-smart-engine-runs";

interface SmartEngineRunsPanelProps {
  associatedSpaceAccountId: string;
  smartViewId?: string;
  smartContextId?: string;
}

const formatTimestamp = (iso?: string | null) => {
  if (!iso) {
    return "–";
  }
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  if (!dt.isValid) {
    return iso;
  }
  const relative = dt.toRelative({ base: DateTime.utc(), style: "short" });
  const absolute = dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  return relative ? `${relative} (${absolute})` : absolute;
};

const statusTone = (status: string) => {
  switch (status) {
    case "SUCCEEDED":
      return "green";
    case "FAILED":
      return "red";
    default:
      return "gray";
  }
};

export const SmartEngineRunsPanel: FC<SmartEngineRunsPanelProps> = ({
  associatedSpaceAccountId,
  smartViewId,
  smartContextId,
}) => {
  const runsQuery = useSmartEngineRuns({
    filter_by_space_account_id: associatedSpaceAccountId,
    filter_by_smart_view_id: smartViewId,
    filter_by_smart_context_id: smartContextId,
    limit: 25,
    enabled: Boolean(associatedSpaceAccountId),
  });

  const runs = runsQuery.data?.results ?? [];

  const sortedRuns = useMemo<SmartEngineRunSummary[]>(() => {
    const parseMillis = (iso?: string | null) => {
      const dt = DateTime.fromISO(iso ?? "");
      return dt.isValid ? dt.toMillis() : 0;
    };
    return [...runs].sort((a, b) => parseMillis(b.started_at) - parseMillis(a.started_at));
  }, [runs]);

  const hasData = sortedRuns.length > 0;

  return (
    <Box>
      <Flex justify="between" align="center" mb="2">
        <Text as="div" size="2" weight="bold">
          Recent Smart Engine Runs
        </Text>
        <Button
          size="1"
          variant="soft"
          disabled={runsQuery.isRefetching}
          onClick={() => runsQuery.refetch()}
        >
          {runsQuery.isRefetching ? "Refreshing…" : "Refresh"}
        </Button>
      </Flex>

      {runsQuery.isLoading && <Text size="1">Loading runs…</Text>}
      {runsQuery.isError && (
        <Text size="1" color="red">
          Failed to load runs.
        </Text>
      )}

      {!runsQuery.isLoading && !hasData && !runsQuery.isError && (
        <Text size="1" color="gray">
          No runs recorded yet.
        </Text>
      )}

      {hasData && (
        <Table.Root size="1" variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Mode</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Started</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedRuns.map(run => (
              <Table.Row key={run.id}>
                <Table.Cell>
                  <Badge size="1" color={statusTone(run.status)}>
                    {run.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{run.mode}</Table.Cell>
                <Table.Cell>{formatTimestamp(run.started_at)}</Table.Cell>
                <Table.Cell>
                  <Text as="span" size="1" color="gray">
                    {run.id}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Button asChild size="1" variant="outline">
                    <Link
                      to={
                        atlasRoutes.smartEngineRunsView({
                          params: { smartEngineRunId: run.id },
                        }).path
                      }
                    >
                      View details
                    </Link>
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Box>
  );
};
